import React from "react";
import { Formik } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";

import SelectReceivingModalForm from "./SelectReceivingModalForm";
import useAuth from "../../hooks/useAuth";
import PreserveReceivingFormState from "./PreserveReceivingFormState";
import useQueryParams from "../../hooks/useQueryParams";
import moment from "moment";

export default function SelectReceivingModal({
  name,
  title,
  buttonLabel,
  description,
  selectedClientId,
  selectedProjectId,
  onSubmit,
  onClose,
  showReceivingNotes = true,
  alwaysShowDates = false,
  simpleValidation = false,
  reportType,
  inventoryAsOfDate = false,
  reportMode,
  isPredefined = false,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const warehouseDropdownEnabled = settings.warehouse_id_dropdown_menu;
  const queryParams = useQueryParams();
  const urlProjectId = queryParams.get("projectId");
  const urlStartDate = queryParams.get("startDate");
  const urlEndDate = queryParams.get("endDate");

  const initialValues = {
    clientId: selectedClientId,
    projectId: urlProjectId || selectedProjectId,
    projectIds: [],
    startDate:
      urlStartDate && moment(urlStartDate, "YYYY-MM-DD", true).isValid()
        ? moment(urlStartDate, "YYYY-MM-DD")
        : null,
    endDate:
      urlEndDate && moment(urlEndDate, "YYYY-MM-DD", true).isValid()
        ? moment(urlEndDate, "YYYY-MM-DD")
        : null,
    inventoryAsOfDate: null,
    receivingNotes: "",
    warehouseId: [],
    costCenterId: [],
    assetFieldFilters: [],
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    const isOperationalActivityMode =
      reportMode === "operational_activity" ||
      reportType === "multi_project_billing_operational_activity";
    // Check if "all projects" is selected (company-wide report)
    const isAllProjects = values.projectId === "all" || values.projectIds === "all";
    // Check if "all clients" is selected (company-wide report)
    const isAllClients = values.clientId === "null";
    // Check if specific multiple projects are selected (2 or more)
    const isMultiProject =
      values.projectIds && Array.isArray(values.projectIds) && values.projectIds.length > 1;
    // Check if exactly one project is selected from the array
    const isSingleProjectFromArray =
      values.projectIds && Array.isArray(values.projectIds) && values.projectIds.length === 1;

    const submitData = {
      // For company-wide reports or warehouse dropdown mode, clientId should be null.
      // Top-level "Select All" sets projectIds to "all", which should also clear
      // client scope so we use the company-wide mutation path.
      clientId: warehouseDropdownEnabled || isAllClients || isAllProjects ? null : values.clientId,
      // For company-wide reports, both projectId and projectIds should be null
      // If single project from array, extract it to projectId
      projectId: isOperationalActivityMode
        ? null
        : isAllProjects
        ? null
        : isSingleProjectFromArray
        ? values.projectIds[0]
        : !isMultiProject
        ? values.projectId
        : null,
      projectIds: isOperationalActivityMode ? null : (isMultiProject ? values.projectIds : null),
      receivingNotes: values.receivingNotes,
      startDate: values.startDate,
      endDate: values.endDate,
      inventoryAsOfDate: values.inventoryAsOfDate,
      warehouseId:
        Array.isArray(values.warehouseId) && values.warehouseId.length > 0
          ? values.warehouseId
          : null,
      assetFieldFilters: values.assetFieldFilters || [],
    };

    // Only include costCenterId if it's selected
    if (Array.isArray(values.costCenterId) && values.costCenterId.length > 0) {
      submitData.costCenterId = values.costCenterId;
    }

    onSubmit(submitData);
    setSubmitting(false);
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box width={48} />
          <Typography variant="h2" color="textSecondary" component="span">
            {title}
          </Typography>
          <IconButton color="textPrimary" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Formik initialValues={initialValues} onSubmit={handleFormSubmit}>
            <>
              <PreserveReceivingFormState />
              <SelectReceivingModalForm
                name={name}
                buttonLabel={buttonLabel}
                description={description}
                showReceivingNotes={showReceivingNotes}
                alwaysShowDates={alwaysShowDates}
                simpleValidation={simpleValidation}
                reportType={reportType}
                inventoryAsOfDate={inventoryAsOfDate}
                reportMode={reportMode}
                warehouseDropdownEnabled={warehouseDropdownEnabled}
                isPredefined={isPredefined}
              />
            </>
          </Formik>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
}
